// src/components/FavoritePetCard.js

import React, { useContext, useEffect, useMemo, useState } from 'react';
import './FavoritePetCard.css';
import { renderGenderIcon } from '../utils/genderIcons';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
// ➕ Usamos el mismo hook de React Query con optimistic update
import { useToggleLikeRQ } from '../hooks/rq/useToggleLike';
import { ModalContext } from '../context/ModalContext';
import { TabContext } from '../context/TabContext';
// 📍 Importamos contextos para calcular la ubicación efectiva:
import { AuthContext }     from '../context/AuthContext';
import { FilterContext }   from '../context/FilterContext';
import { LocationContext } from '../context/LocationContext';
import ModalPetDetail from '../components/ModalPetDetail';

// ────────────────────────────────────────────────────────────────
// Utilidad local: calcula la edad a partir de la fecha de nacimiento
function calculateAge(birthDate) {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const now   = new Date();
  let years   = now.getFullYear() - birth.getFullYear();
  let months  = now.getMonth()  - birth.getMonth();
  let days    = now.getDate()   - birth.getDate();
  if (days < 0) months--;
  if (months < 0) { years--; months += 12; }
  if (years < 1) return { type: 'months', value: Math.max(months, 1) };
  return { type: 'years', value: years };
}
// ────────────────────────────────────────────────────────────────

/**
 * FavoritePetCard
 */
const FavoritePetCard = ({ pet }) => {
  // ─── Logs de distancia ──────────────────────────────────────
  useEffect(() => {
    //console.log(`[FavoritePetCard] pet.id=${pet.id}  distancia=${pet.distance ?? 'null'}`);
  }, [pet.distance, pet.id]);

  // ─── Derivar ubicación efectiva ─────────────────────────────
  const { user }    = useContext(AuthContext);
  const { filters } = useContext(FilterContext);
  const { coords }  = useContext(LocationContext);
  const effectiveLocation = useMemo(() => {
    if (user?.latitude != null && user?.longitude != null) {
      return { lat: user.latitude, lng: user.longitude };
    }
    if (filters.location?.lat != null && filters.location?.lng != null) {
      return { lat: filters.location.lat, lng: filters.location.lng };
    }
    return { lat: coords.lat, lng: coords.lng };
  }, [
    user?.latitude, user?.longitude,
    filters.location?.lat, filters.location?.lng,
    coords.lat, coords.lng
  ]);

  // ─── Mutación de like con optimistic update ────────────────
  const {
    mutate: toggleLike,
    isLoading: isToggling
  } = useToggleLikeRQ(effectiveLocation);

  // ─── Estado de animación de corazón ─────────────────────────
  const [animateHeart, setAnimateHeart] = useState(false);

  // Estatus visual según dato de la pet
  const isFavorite = pet.liked_by_user;

  // ─── Handler para quitar/marcar favorito ────────────────────
  const handleLikeClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setAnimateHeart(true);               // inicia animación
    toggleLike({ petId: pet.id });       // lanza mutación
  };

  // ─── Abrir detalle de mascota ──────────────────────────────
  const { openModal, modalStacks } = useContext(ModalContext);
  const { activeTab }              = useContext(TabContext);
  const handleCardClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (modalStacks[activeTab]?.find(m => m.metadata?.petId === pet.id)) return;
    openModal(
      <ModalPetDetail petId={pet.id} />,
      {
        tab: activeTab,
        metadata: { petId: pet.id, petName: pet.nombre, petSexo: pet.sexo },
        overlayClassName: 'pet-detail-overlay'
      }
    );
  };

  // ─── Extra-info (edad, raza/tamaño) ────────────────────────
  let ageText = null;
  const ageObj = calculateAge(pet.f_nacimiento);
  if (ageObj) {
    ageText = ageObj.type === 'years'
      ? `${ageObj.value} año${ageObj.value > 1 ? 's' : ''}`
      : `${ageObj.value} mes${ageObj.value > 1 ? 'es' : ''}`;
  }
  const secondVar = pet.raza1 || pet.size;
  const extraInfo = [];
  if (ageText) {
    extraInfo.push(ageText);
    if (secondVar) extraInfo.push(secondVar);
  } else if (secondVar) {
    extraInfo.push(secondVar);
  }

  return (
    <div className="favorite-pet-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      {/* Imagen */}
      <div className="favorite-pet-image-container">
        {pet.fotos?.length ? (
          <img src={pet.fotos[0].url_imagen} alt={pet.nombre}
               className="favorite-pet-image" loading="lazy" />
        ) : (
          <div className="favorite-pet-image-placeholder">Sin Imagen</div>
        )}
      </div>

      {/* Info */}
      <div className="favorite-pet-info">
        <div className="professional-info-row">
          <div className="professional-left">
            {pet.profesional?.logo
              ? <img src={pet.profesional.logo} alt={pet.profesional.nombre}
                     className="professional-favCard-logo" loading="lazy" />
              : <div className="professional-logo-placeholder" />}
            <span className="professional-favCard-name">{pet.profesional?.nombre}</span>
          </div>
          <div className="professional-right">
            <span className="distance-text">
              {pet.distance ? `a ${pet.distance} Kms.` : 'a -- Kms.'}
            </span>
          </div>
        </div>

        <div className="pet-info-row">
          <div className="pet-left">
            {renderGenderIcon(pet.sexo)}
            <span className="pet-favCar-name">{pet.nombre}</span>
          </div>
        </div>

        {extraInfo.length > 0 && (
          <div className="pet-extra-info-row">
            {extraInfo.map((txt, i) => (
              <span key={i} className={`extra-info-item${i === 1 ? ' truncate' : ''}`}>
                {txt}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Botón Like con animación */}
      <div className="like-button-container" onClick={e => e.stopPropagation()}>
        <button
          className="like-button"
          onClick={handleLikeClick}
          disabled={isToggling}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          {isFavorite ? (
            <FaHeart
              className={`like-icon filled${animateHeart ? ' heart-beat' : ''}`}
              onAnimationEnd={() => setAnimateHeart(false)}
            />
          ) : (
            <FaRegHeart
              className={`like-icon outline${animateHeart ? ' heart-beat' : ''}`}
              onAnimationEnd={() => setAnimateHeart(false)}
            />
          )}
        </button>
      </div>
    </div>
  );
};

export default FavoritePetCard;
